import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { SlidersHorizontal } from "lucide-react";

const SORTS = [
  { v: "popular", label: "Popular" },
  { v: "newest", label: "Newest" },
  { v: "price_asc", label: "Price: Low → High" },
  { v: "price_desc", label: "Price: High → Low" },
  { v: "rating", label: "Highest rated" },
];

function resolveOptions(filter, careLevels) {
  if (filter.options_source === "care_levels") return careLevels.map((c) => c.label);
  return filter.options || [];
}

function toggleArrayValue(current, value) {
  const arr = Array.isArray(current) ? current : [];
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

function cleanAttrs(attrs) {
  return Object.fromEntries(
    Object.entries(attrs).filter(([, v]) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== "" && v !== false;
    }),
  );
}

const PAGE_SIZE = 60;

export default function Shop() {
  const { category } = useParams();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const collection = sp.get("collection") || undefined;
  const titleOverride = sp.get("title") || "";
  const [sort, setSort] = useState("popular");
  const [priceRange, setPriceRange] = useState(null);
  const [attrs, setAttrs] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [globalFilters, setGlobalFilters] = useState({ price_filter: { ranges: [] }, special_discovery_menus: {} });
  const [careLevels, setCareLevels] = useState([]);

  useEffect(() => {
    Promise.all([api.categories(), api.filters.global(), api.careLevels()])
      .then(([cats, gf, cl]) => {
        setCategories(cats);
        setGlobalFilters(gf);
        setCareLevels(cl);
      })
      .catch(() => {});
  }, []);

  // A different category/collection has a different set of filters — start clean when it changes.
  useEffect(() => {
    setAttrs({});
    setPriceRange(null);
  }, [category, collection]);

  const buildParams = (pageNum) => ({
    search: q, category, collection, sort,
    min_price: priceRange?.min, max_price: priceRange?.max,
    attributes: cleanAttrs(attrs),
    page: pageNum, page_size: PAGE_SIZE,
  });

  // Filter/search/sort change: reset to page 1 and replace the list. Kept separate from
  // loadMore (below) so a filter change mid-"load more" can't race and append a stale page
  // onto a list that was reset out from under it — `cancelled` guards the async gap.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setPage(1);
      try {
        const res = await api.products(buildParams(1));
        if (!cancelled) { setItems(res.items); setTotal(res.total); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, collection, q, sort, priceRange, attrs]);

  const loadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await api.products(buildParams(nextPage));
      setItems((prev) => [...prev, ...res.items]);
      setTotal(res.total);
      setPage(nextPage);
    } finally { setLoadingMore(false); }
  };

  const currentCategory = categories.find((c) => c.id === category);
  const filters = currentCategory?.filters || [];
  const priceRanges = globalFilters.price_filter?.ranges || [];

  const title = q
    ? `Results for "${q}"`
    : titleOverride
      ? titleOverride
      : currentCategory?.name
        ? currentCategory.name
        : category
          ? category.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
          : "All plants";

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-4 py-10 md:py-14">
      <Seo
        title={q ? title : `Buy ${title} Online`}
        description={`Browse ${title.toLowerCase()} on YourPlants.in — quality plants from trusted local nurseries, delivered to your doorstep with Pay After Check.`}
        path={category ? `/shop/${category}` : "/shop"}
      />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Shop</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--yp-primary)]">{title}</h1>
          <div className="text-sm text-[var(--yp-muted)] mt-1">{total} results</div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="toggle-filters" onClick={() => setShowFilters((v) => !v)} className="md:hidden inline-flex items-center gap-2 border border-[var(--yp-border)] rounded-full px-4 py-2 text-sm">
            <SlidersHorizontal size={16} /> Filters
          </button>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[var(--yp-border)] rounded-full px-4 py-2 text-sm bg-white">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <aside data-testid="filters-panel" className={`${showFilters ? "block" : "hidden"} md:block`}>
          <div className="bg-white border border-[var(--yp-border)] rounded-2xl p-5">
            <div className="font-serif-display text-lg text-[var(--yp-primary)]">Filters</div>

            {filters.map((f) => (
              <div className="mt-4" key={f.key}>
                <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)] mb-2">{f.label}</div>
                {f.type === "boolean" ? (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid={`filter-${f.key}`}
                      type="checkbox"
                      checked={!!attrs[f.key]}
                      onChange={(e) => setAttrs((prev) => ({ ...prev, [f.key]: e.target.checked }))}
                      className="accent-[var(--yp-primary)]"
                    />
                    {f.label}
                  </label>
                ) : (
                  <div className="space-y-2">
                    {resolveOptions(f, careLevels).map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          data-testid={`filter-${f.key}-${opt}`}
                          type="checkbox"
                          checked={
                            f.type === "multi_select"
                              ? (Array.isArray(attrs[f.key]) && attrs[f.key].includes(opt))
                              : attrs[f.key] === opt
                          }
                          onChange={() =>
                            setAttrs((prev) => ({
                              ...prev,
                              [f.key]: f.type === "multi_select"
                                ? toggleArrayValue(prev[f.key], opt)
                                : (prev[f.key] === opt ? undefined : opt),
                            }))
                          }
                          className="accent-[var(--yp-primary)]"
                        />
                        {opt}
                      </label>
                    ))}
                    {resolveOptions(f, careLevels).length === 0 && (
                      <div className="text-xs text-[var(--yp-muted)]">No options configured yet.</div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)] mb-2">Price</div>
              <div className="flex flex-wrap gap-2">
                {priceRanges.map((r) => {
                  const active = priceRange?.min === (r.min ?? undefined) && priceRange?.max === (r.max ?? undefined);
                  return (
                    <button
                      key={r.label}
                      data-testid={`filter-price-${r.label}`}
                      onClick={() => setPriceRange(active ? null : { min: r.min, max: r.max })}
                      className={`chip ${active ? "!bg-[var(--yp-primary)] !text-white !border-[var(--yp-primary)]" : ""}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="clear-filters"
              onClick={() => { setAttrs({}); setPriceRange(null); }}
              className="mt-6 text-xs text-[var(--yp-primary)] hover:underline"
            >
              Clear all
            </button>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="text-center py-24 text-[var(--yp-muted)]">Loading plants…</div>
          ) : items.length === 0 ? (
            <div data-testid="no-results" className="text-center py-24 text-[var(--yp-muted)]">No plants match your filters yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 stagger">
                {items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {items.length < total && (
                <div className="mt-8 text-center">
                  <button
                    data-testid="btn-load-more"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 border border-[var(--yp-border)] rounded-full px-6 py-3 text-sm font-medium hover:border-[var(--yp-primary)] disabled:opacity-60"
                  >
                    {loadingMore ? "Loading…" : `Load more (${items.length} of ${total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
